import { CommonModule } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatTabsModule } from '@angular/material/tabs';
import { SfoUiJSONSchema7 } from '../../metadata.model';

import { SfoAdmonitionComponent } from '../../widgets';
import { DynamicFieldModule } from '../form-fields';
import { MarkdownPipe } from '../markdown.pipe';
import { RenderMode } from '../types/view.types';

interface TabConfig {
  label: string;
  controls: string[]; // a group may contain multiple controls
  description?: string;
  icon?: string;
  isGroup?: boolean;
}

@Component({
  selector: 'sfo-tab-view',
  templateUrl: './tab-view.component.html',
  styleUrls: ['./tab-view.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatTabsModule,
    DynamicFieldModule,
    SfoAdmonitionComponent,
    MarkdownPipe,
  ],
})
export class TabViewComponent implements OnInit {
  @Input() schema: SfoUiJSONSchema7 | null;
  @Input() aFormControl: FormGroup;
  @Input() renderMode: RenderMode | undefined;
  @Input() activeTab?: string | number;

  tabs: TabConfig[] = [];
  selectedTabIndex = 0;

  ngOnInit(): void {
    this.generateTabs();
    this.setInitialActiveTab();
  }

  private generateTabs(): void {
    const properties = this.schema?.properties || {};
    const groupedFields = new Map<string, string[]>();

    Object.entries(properties).forEach(([key, value]: [string, any]) => {
      if (value?.['_ui_hide']) {
        return;
      }

      const group = value?.['_ui_group'];
      if (group) {
        if (!groupedFields.has(group)) {
          groupedFields.set(group, []);
        }
        groupedFields.get(group)?.push(key);
      } else {
        this.tabs.push({
          label: value.title || key,
          controls: [key],
          description: value.description,
          icon: value['_ui_icon'],
          isGroup: false,
        });
      }
    });

    groupedFields.forEach((controls, groupName) => {
      this.tabs.push({
        label: groupName,
        controls: controls,
        isGroup: true,
      });
    });
  }

  private setInitialActiveTab(): void {
    if (!this.activeTab) return;

    if (typeof this.activeTab === 'number') {
      if (this.activeTab >= 0 && this.activeTab < this.tabs.length) {
        this.selectedTabIndex = this.activeTab;
      }
    } else {
      const tabIndex = this.tabs.findIndex(
        (tab) => tab.label.toLowerCase() === this.activeTab?.toString().toLowerCase(),
      );

      if (tabIndex !== -1) {
        this.selectedTabIndex = tabIndex;
      }
    }
  }

  getFormControl(control: string): FormGroup {
    return this.aFormControl.get(control) as FormGroup;
  }
}
